/**
 * useRelatedTasksTypeCounts - Contadores de tarefas por tipo (Normal | Retenção | Jurídico)
 *
 * Usado pelas subabas da aba Tarefas nos modais de Cliente, Negócio, Veículo e no
 * sidebar do Chat. Faz UMA única chamada à RPC get_related_tasks_type_counts, que
 * espelha os filtros de escopo e a visibilidade (can_user_view_task) da listagem.
 *
 * @version 1.1.0
 * RELEASE: 2026-09-26 v1.1.0 - Com veículo, não filtra pelo contato (paridade com a listagem).
 * RELEASE: 2026-08-21 v1.0.0 - Criação inicial
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { TaskModuleType } from '@/hooks/useTaskModuleType';

const logger = createLogger('useRelatedTasksTypeCounts');

/** Contexto aceito (mesmo contrato da TasksRelatedList) */
export interface RelatedTasksContext {
  dealId?: string;
  contactId?: string | null;
  vehicleId?: string | null;
}

/** Mapa de contagem por tipo de tarefa */
export type RelatedTasksTypeCounts = Record<TaskModuleType, number>;

/** Linha retornada pela RPC */
interface CountRow {
  task_type: TaskModuleType;
  total: number;
}

export function useRelatedTasksTypeCounts(context: RelatedTasksContext) {
  const { currentTenant } = useAuth();

  const dealId = context.dealId ?? null;
  // v1.1.0: com veículo, ignora o contato (mesma regra da TasksRelatedList)
  const vehicleIdRaw = context.vehicleId ?? null;
  const contactId = vehicleIdRaw ? null : (context.contactId ?? null);
  const vehicleId = context.vehicleId ?? null;
  const hasContext = !!(dealId || contactId || vehicleId);

  return useQuery<RelatedTasksTypeCounts>({
    queryKey: ['tasks-related-type-counts', currentTenant?.id, dealId, contactId, vehicleId],
    queryFn: async () => {
      const empty: RelatedTasksTypeCounts = { normal: 0, retention: 0, legal: 0 };
      if (!hasContext) return empty;

      const { data, error } = await supabase.rpc('get_related_tasks_type_counts', {
        p_deal_id: dealId,
        p_contact_id: contactId,
        p_vehicle_id: vehicleId,
        p_tenant_hint: currentTenant?.id || null,
      } as any);

      if (error) {
        logger.error('Erro ao buscar contadores de tarefas por tipo', error);
        throw error;
      }

      // Consolida as linhas (a RPC só retorna tipos com pelo menos 1 tarefa)
      return ((data || []) as CountRow[]).reduce<RelatedTasksTypeCounts>((acc, row) => {
        if (row.task_type in acc) acc[row.task_type] = Number(row.total) || 0;
        return acc;
      }, { ...empty });
    },
    enabled: !!currentTenant?.id && hasContext,
    staleTime: 30_000,
  });
}
